import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause } from 'lucide-react';
import { callDemos } from '../data/mock';

const CallDemos = () => {
  const [playingId, setPlayingId] = useState(null);
  const [progress, setProgress] = useState({});
  const audioRefs = useRef({});
  const containerRef = useRef(null);

  const getAudioRef = (demoId) => {
    if (!audioRefs.current[demoId] && containerRef.current) {
      const el = containerRef.current.querySelector(`audio[data-demo-id="${demoId}"]`);
      if (el) {
        audioRefs.current[demoId] = el;
      }
    }
    return audioRefs.current[demoId];
  };

  useEffect(() => {
    return () => {
      Object.values(audioRefs.current).forEach(audio => {
        audio.pause();
      });
    };
  }, []);

  const handleTimeUpdate = (demoId, e) => {
    setProgress(prev => ({ ...prev, [demoId]: e.target.currentTime }));
  };

  const handleEnded = (demoId) => {
    setPlayingId(null);
    setProgress(prev => ({ ...prev, [demoId]: 0 }));
  };

  const togglePlay = (demoId) => {
    const audio = getAudioRef(demoId);
    if (!audio) return;

    if (playingId === demoId) {
      audio.pause();
      setPlayingId(null);
    } else {
      if (playingId) {
        const prev = getAudioRef(playingId);
        if (prev) { prev.pause(); prev.currentTime = 0; }
      }
      audio.play().catch(err => console.log('Audio play failed:', err));
      setPlayingId(demoId);
    }
  };

  const getColorClasses = (color) => {
    const colors = {
      teal: 'from-teal-500 to-teal-600',
      blue: 'from-blue-500 to-blue-600',
      orange: 'from-orange-500 to-orange-600',
      purple: 'from-purple-500 to-purple-600'
    };
    return colors[color] || colors.teal;
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <section id="demos" className="py-16 px-6" data-testid="call-demos-section" ref={containerRef}>
      <div className="max-w-7xl mx-auto">
        {/* Hidden audio elements */}
        {callDemos.map((demo) => (
          <audio
            key={`audio-${demo.id}`}
            data-demo-id={demo.id}
            src={`/audio/demo${demo.id}.mp3`}
            preload="metadata"
            onTimeUpdate={(e) => handleTimeUpdate(demo.id, e)}
            onEnded={() => handleEnded(demo.id)}
          />
        ))}
        {/* Section Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-white mb-2" data-testid="demos-heading">
            Hear Miadesk in Action
          </h2>
          <p className="text-gray-400">
            Tap play to hear examples of how Miadesk handles real customer calls.
          </p>
        </div>

        {/* Demo Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {callDemos.map((demo) => (
            <div
              key={demo.id}
              data-testid={`demo-card-${demo.id}`}
              className="bg-gradient-to-br from-gray-900/50 to-gray-800/50 border border-white/10 rounded-2xl p-5 hover:border-purple-500/30 transition-all cursor-pointer backdrop-blur-sm"
            >
              {/* Circular Icon with Animation */}
              <div className="flex justify-center mb-6">
                <div className={`w-24 h-24 rounded-full bg-gradient-to-br ${getColorClasses(demo.color)} flex items-center justify-center relative`}>
                  {/* Animated Ring */}
                  {playingId === demo.id && (
                    <div className={`absolute inset-0 rounded-full bg-gradient-to-br ${getColorClasses(demo.color)} opacity-20 animate-ping`}></div>
                  )}
                  {/* Waveform Line */}
                  <div className="w-0.5 h-12 bg-white rounded-full"></div>
                </div>
              </div>

              {/* Title */}
              <h3 className="text-white font-medium text-center mb-6 text-base leading-snug min-h-[48px]">
                {demo.title}
              </h3>

              {/* Audio Player */}
              <div className="bg-black/40 rounded-xl p-3 border border-white/5">
                <div className="flex items-center gap-3">
                  {/* Play Button */}
                  <button
                    onClick={() => togglePlay(demo.id)}
                    data-testid={`demo-play-btn-${demo.id}`}
                    className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 flex items-center justify-center transition-all shadow-lg flex-shrink-0"
                  >
                    {playingId === demo.id ? (
                      <Pause className="w-4 h-4 text-white fill-white" />
                    ) : (
                      <Play className="w-4 h-4 text-white fill-white ml-0.5" />
                    )}
                  </button>

                  {/* Waveform Visualization */}
                  <div className="flex-1 flex items-center justify-center gap-0.5 h-8">
                    {[...Array(20)].map((_, i) => (
                      <div
                        key={i}
                        className={`w-0.5 rounded-full transition-all ${
                          playingId === demo.id ? 'bg-purple-400' : 'bg-gray-600'
                        }`}
                        style={{
                          height: `${Math.random() * 60 + 20}%`,
                          opacity: playingId === demo.id ? 0.8 + (Math.random() * 0.2) : 0.5
                        }}
                      ></div>
                    ))}
                  </div>

                  {/* Time and Demo Label */}
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-gray-400">
                      {formatTime(progress[demo.id] || 0)}
                    </span>
                    <span className="text-gray-500">Demo</span>
                    <span className="text-gray-400">{demo.duration}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Note about audio */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Press play to hear how Mia handles real business calls
          </p>
        </div>
      </div>
    </section>
  );
};

export default CallDemos;